import type {
  AllAccountsAsOf,
  PortfolioOverview,
} from '../model/portfolioOverview'
import { countOf, dateRangeText } from '../portfolioText'
import { VerdictBadge } from '../VerdictBadge'
import { HeroFigure } from './HeroFigure'
import { chartLabel } from './heroText'
import { MoneyInOut } from './MoneyInOut'
import { TotalOverTimeChart } from './TotalOverTimeChart'

interface AllAccountsCardProps {
  overview: PortfolioOverview
  asOf: AllAccountsAsOf
  isLedgerIncomplete: boolean
}

const MIN_CHART_POINTS = 2

const headline = (asOf: AllAccountsAsOf) =>
  [
    countOf(asOf.accountCount, 'account'),
    countOf(asOf.statementCount, 'statement'),
    asOf.period && dateRangeText(asOf.period),
  ]
    .filter(Boolean)
    .join(' · ')

// The hero: the total of every account on the as-of date, how it moved, and the money in and out.
export const AllAccountsCard = ({
  overview,
  asOf,
  isLedgerIncomplete,
}: AllAccountsCardProps) => (
  <section className="@container rounded-lg border bg-white p-6 text-sm text-blue-7">
    <div className="flex items-start justify-between gap-4">
      <div className="flex flex-wrap items-baseline gap-x-2.5">
        <h3 className="text-base font-semibold">All accounts</h3>
        <span className="text-sm text-ink-subtle">{headline(asOf)}</span>
      </div>
      <VerdictBadge status={asOf.badge.status} sentence={asOf.badge.sentence} />
    </div>
    <div className="mt-6 grid grid-cols-1 gap-x-16 gap-y-8 @4xl:grid-cols-[minmax(0,1fr)_420px]">
      <div className="min-w-0">
        <HeroFigure asOf={asOf} />
        {overview.series.length >= MIN_CHART_POINTS && (
          <TotalOverTimeChart
            series={overview.series}
            asOfMonth={asOf.month}
            label={chartLabel(asOf)}
          />
        )}
      </div>
      {asOf.money.window && asOf.money.totals && (
        <MoneyInOut
          window={asOf.money.window}
          totals={asOf.money.totals}
          notCounted={asOf.money.notCounted}
          accountCount={asOf.accountCount}
          isLedgerIncomplete={isLedgerIncomplete}
        />
      )}
    </div>
  </section>
)
